'use strict';

const { debounce, filter, includes, isUndefined, isEmpty } = require('lodash');
const storage = require('@api/storage').default;

const _ = { debounce, filter, includes, isUndefined, isEmpty };

const OUTLINE_ZINDEX = '10000';
const INTERFACE_OUTLINE_ZINDEX = '10001';

const TOOLTIP_ARROW_OFFSET = 8;
const TOOLTIP_ARROW_OUTER_OFFSET = 15;
const ON_SCROLL_DELAY_MS = 100;
const TOOLTIP_PADDING_FROM_BOTTOM = 40;
const TOOLTIP_X_PADDING_DEFAULT = 10;
const TOOLTIP_Y_PADDING_DEFAULT = 10;
const TOOLTIP_DELAY = 350; // hacky way to wait for the tooltip element to resize with its content before trying to reposition

function Coachmark() {
  let steps = null;
  let tooltip = null;
  let nextBtn = null;
  let prevBtn = null;
  let skipBtn = null;
  let gotItButton = null;
  let currentStep = 0;
  let highlighter = null;
  let tooltipTextElement = null;
  const styleClass = 'coachmark-styles';
  let options = null;
  let tutIsRunning = false;

  const overlayTopStyles = `
    top: 0;
    left: 0;
    width: 100%;
    position: fixed;
    background-color: rgb(0, 0, 0, 0.5);
    z-index: ${OUTLINE_ZINDEX};
    transition: all 0.4s ease;
  `;

  const overlayBottomStyles = `
    left: 0;
    width: 100%;
    height: 1000%;
    position: fixed;
    background-color: rgb(0, 0, 0, 0.5);
    z-index: ${OUTLINE_ZINDEX};
    transition: all 0.4s ease;
  `;

  const overlayLeftStyles = `
    left: 0;
    position: fixed;
    background-color: rgb(0, 0, 0, 0.5);
    z-index: ${OUTLINE_ZINDEX};
    transition: all 0.4s ease;
  `;

  const overlayRightStyles = `
    position: fixed;
    width: 100%;
    background-color: rgb(0, 0, 0, 0.5);
    z-index: ${OUTLINE_ZINDEX};
    transition: all 0.4s ease;
  `;

  const interfaceStyles = `
    position: fixed;
    z-index: ${INTERFACE_OUTLINE_ZINDEX};
    transition: all 0.4s ease;
    background-color: transparent;
    pointer-events: all;
  `;

  const highlightedElementStyles = `
    position: relative;
    z-index: 9999;
  `;

  const tooltipStyles = `
    padding: 1em;
    display: flex;
    z-index: 9999999;
    position: fixed;
    flex-direction: column;
    transition: all 0.4s ease;
    background-color: #17a2b8;
    border-radius: 8px;
    box-shadow: rgba(0, 0, 0, 0.117647) 0px 1px 6px, rgba(0, 0, 0, 0.117647) 0px 1px 4px;
    max-width: 600px;
    color: white;
    opacity: 0;
    --pseudo-top: -8px;
  `;
  const tooltipPseudoStyle = `
    top: var(--pseudo-top);
    left: var(--pseudo-left);
    content: '';
    width: 15px;
    height: 15px;
    position: absolute;
    background-color: #17a2b8;
    transform: rotate(45deg);
  `;

  const buttons = `
    display: flex;
    padding-top: 1rem;
    align-items: center;
    justify-content: space-between;
    min-width: 200px;
  `;

  const btnStyle = `
    padding: 0.5rem;
    font-size: 0.9rem;
    border-radius: 5px;
    border: 1px solid #666;
    -moz-appearance: none;
    background-color: #fff;
    -webkit-appearance: none;
  `;

  /**
   * Inits and starts the tutorial
   */
  function init(optionsParam) {
    options = optionsParam;
    const coachmarkSelectors = document.querySelectorAll("*[data-coachmark]");

    // Sort the coachmarks according to their data attributes so that user can decide
    // the elements priority.
    const coachmarks = Array.from(coachmarkSelectors) || [];

    const viewed = JSON.parse(storage.getItem(storage.TUTORIAL_VIEWED) || JSON.stringify({}));

    const showType = options.type;
    const targets = options.targets || [];
    steps = sortArr(_.filter(coachmarks, (element) => {
      const isViewable = element.offsetWidth > 0;
      const isCorrectType = element.dataset.coachmarkType === showType;
      const isTarget = _.isUndefined(element.dataset.coachmarkTarget) || _.includes(targets, element.dataset.coachmarkTarget);
      const hasViewed = _.includes(viewed[element.dataset.coachmarkType], Number.parseInt(element.dataset.coachmark, 10));
      return isViewable && isCorrectType && isTarget && !hasViewed;
    }));

    if (_.isEmpty(steps)) {
      return;
    }

    tutIsRunning = true;

    const coachmarkElements = getCoachmarkElements();
    document.body.insertAdjacentHTML("beforeend", coachmarkElements);

    highlighter = document.getElementById("js-coachmark-interface");
    tooltip = document.getElementById("js-coachmark-tooltip");
    tooltipTextElement = document.getElementById("js-coachmark-tooltip-text");
    skipBtn = document.querySelector(".coachmark-skip");
    nextBtn = document.querySelector(".coachmark-next");
    prevBtn = document.querySelector(".coachmark-prev");
    gotItButton = document.querySelector(".coachmark-gotit");

    // Add styles for coachmark overlay
    if (options?.lockScroll) {
      addStyles('body', 'overflow: hidden');
    }
    else {
      window.addEventListener('scroll', onScrollEvent);
    }

    addStyles(".coachmark-overlay-top", overlayTopStyles);
    addStyles(".coachmark-overlay-bottom", overlayBottomStyles);
    addStyles(".coachmark-overlay-left", overlayLeftStyles);
    addStyles(".coachmark-overlay-right", overlayRightStyles);
    addStyles(".coachmark-tooltip", tooltipStyles);
    addStyles(".coachmark-interface", interfaceStyles);
    addStyles(".coachmark-tooltip .coachmark-btns", buttons);
    addStyles(".coachmark-tooltip:before", tooltipPseudoStyle);
    addStyles(".coachmark-highlight", highlightedElementStyles);
    addStyles(".coachmark-tooltip .coachmark-btns .__btn", btnStyle);

    highlightElement(steps[currentStep]);
    skipBtn.addEventListener("click", handleSkipClick);
  }

  /**
   * Does calculations to set up the highlighted and overlay positions.  This will also be called when scrolling.
   *
   * @param {*} element   The element to be highlighted
   */
  function setHighlighterOverlay(element) {
    const { top, left } = element.getBoundingClientRect();
    const height = element.offsetHeight;
    const width = element.offsetWidth;
    const tooltipText = element.dataset.coachmarkText || "";

    /**
     * getBoundingClientRect returns the element's position
     * relative to the viewport, not from the body. So it does
     * NOT account for scrollPosition.
     */
    const yScrollPosition = window.pageYOffset;
    const xScrollPosition = window.pageXOffset;

    // determine any padding specifics
    let yPadding = TOOLTIP_Y_PADDING_DEFAULT;
    try {
      if (element.dataset.coachmarkYpadding) {
        yPadding = parseFloat(element.dataset.coachmarkYpadding);
      }
    }
    catch (_) {
      console.error();
    }
    let xPadding = TOOLTIP_X_PADDING_DEFAULT;
    try {
      if (element.dataset.coachmarkXpadding) {
        xPadding = parseFloat(element.dataset.coachmarkXpadding);
      }
    }
    catch (_) {
      console.error();
    }

    if (highlighter) {
      const highlighterTop = top + yScrollPosition - yPadding;
      const hightlighterHeight = height + (yPadding * 2);
      const hightligtherLeft = left + xScrollPosition - xPadding;
      const hightlighterWidth = width + (xPadding * 2);

      highlighter.style["background-color"] = 'transparent';
      highlighter.style.top = `${highlighterTop}px`;
      highlighter.style.left = `${hightligtherLeft}px`;
      highlighter.style.width = `${hightlighterWidth}px`;
      highlighter.style.height = `${hightlighterHeight}px`;
      highlighter.style.borderRadius = '8px';
      highlighter.style.zIndex = INTERFACE_OUTLINE_ZINDEX;

      // figure out the left/top/right/bottom shaded positions
      const overlayLeftWidth = left + xScrollPosition - xPadding;
      const overlayLeftX = left - overlayLeftWidth - xPadding;
      const overlayRightX = left + width + xPadding;

      // update the overlay positions
      const overlayTop = document.getElementById('js-coachmark-overlay-top');
      if (overlayTop) {
        overlayTop.style.height = `${highlighterTop - yScrollPosition}px`;
      }
      const overlayBottom = document.getElementById('js-coachmark-overlay-bottom');
      if (overlayBottom) {
        overlayBottom.style.top = `${highlighterTop + hightlighterHeight - yScrollPosition}px`;
      }
      const overlayLeft = document.getElementById('js-coachmark-overlay-left');
      if (overlayLeft) {
        overlayLeft.style.left = `${overlayLeftX}px`;
        overlayLeft.style.top = `${highlighterTop - yScrollPosition}px`;
        overlayLeft.style.width = `${overlayLeftWidth}px`;
        overlayLeft.style.height = `${hightlighterHeight}px`;
      }
      const overlayRight = document.getElementById('js-coachmark-overlay-right');
      if (overlayRight) {
        overlayRight.style.top = `${highlighterTop - yScrollPosition}px`;
        overlayRight.style.left = `${overlayRightX}px`;
        overlayRight.style.height = `${hightlighterHeight}px`;
      }
    }

    addTooltip({
      top,
      left,
      height,
      text: tooltipText,
      xPadding,
      yPadding
    });
  }

  /**
   * Highlights the element, this is the next element to be moving to
   */
  function highlightElement(element) {
    if (!element) {
      throw new Error(
        `To start using Coachmark plugin, please add data-coachmark attribute.`
      );
    }

    // scroll the element into view first, then we can determine where to set the overlay depending on scroll offset
    scrollToElement(element, () => {
      setHighlighterOverlay(element);

      // Add class to the element
      element.classList.add("coachmark-highlight");

      if (currentStep === steps.length - 1) {
        nextBtn.style.display = "none";
        prevBtn.style.display = "none";
        skipBtn.style.display = 'none';
        gotItButton.style.display = 'inline-block';
        gotItButton.addEventListener('click', handleGotItClick);
      }
      else {
        nextBtn.style.display = "inline-block";
        nextBtn.addEventListener("click", handleNextBtnClick);
        if (currentStep > 0) {
          prevBtn.style.display = "none";
          prevBtn.addEventListener("click", handlePrevBtnClick);
        }
        else {
          prevBtn.style.display = "none";
        }
        gotItButton.style.display = 'none';
      }
    });
  }

  /**
   * Scroll the element into view so we can highlight it.  callback is called when it's done
   * scrolling.
   */
  function scrollToElement(element, callback) {
    const rect = element.getBoundingClientRect();

    // determine if we need to move into view; try to scroll if on bottom half of the page
    if (rect.top + rect.height >= window.innerHeight / 2) {
      const onScroll = function () {
        window.removeEventListener('scroll', debounced)
        callback();
      };
      const debounced = _.debounce(onScroll, ON_SCROLL_DELAY_MS);

      window.addEventListener('scroll', debounced);
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
      debounced();
    }
    else {
      callback();
    }
  }

  /**
   * Unhighlight the element
   */
  function removeHighlight(element) {
    if (element) {
      element.classList.remove("coachmark-highlight");
    }
  }

  /**
   * Add the tooltip to the screen.  Default position is underneath the element, will place on top if there isn't
   * enough space underneath
   */
  function addTooltip({ top, left, height, text, xPadding, yPadding }) {
    const topNum = Number.parseInt(top, 10);
    const heightNum = Number.parseInt(height, 10);
    const padding = 10;

    if (text === "") {
      console.warn("Please add text for coachmark tooltip");
    }

    if (text) {
      tooltipTextElement.innerHTML = text;
    }

    tooltip.style.left = `${Math.max(left - xPadding, 0)}px`;
    tooltip.style.opacity = 1;

    // determine if there's enough space so it doesnt go offscreen.  need to give it time to resize the tooltip to determine height
    setTimeout(() => {
      const shouldBeOnTop = topNum + heightNum + tooltip.offsetHeight + padding >= window.innerHeight - TOOLTIP_PADDING_FROM_BOTTOM;
      if (shouldBeOnTop) {
        tooltip.style.top = `${topNum - tooltip.offsetHeight - yPadding - TOOLTIP_ARROW_OUTER_OFFSET}px`;
        tooltip.style.setProperty("--pseudo-top", `${tooltip.offsetHeight - TOOLTIP_ARROW_OFFSET}px`);
      }
      else {
        tooltip.style.top = `${topNum + heightNum + yPadding + TOOLTIP_ARROW_OUTER_OFFSET}px`;
        tooltip.style.setProperty("--pseudo-top", `-${TOOLTIP_ARROW_OFFSET}px`);
      }

      // if off screen right, nudge it back
      const tooltipRight = tooltip.offsetLeft + tooltip.offsetWidth;
      if (tooltipRight > window.innerWidth) {
        const leftOffset = tooltipRight - window.innerWidth;
        tooltip.style.left = `${tooltip.offsetLeft - leftOffset}px`;
        tooltip.style.setProperty("--pseudo-left", `${leftOffset + TOOLTIP_ARROW_OFFSET}px`);
      }
    }, TOOLTIP_DELAY);
  }

  /**
   * Handle the skip all, needs to mark every step after as viewed.
   */
  function handleSkipClick() {
    // need to mark all as viewed
    for (let i = currentStep; i < steps.length; i++) {
      markAsViewed(steps[i]);
    }

    destroy();
  }

  /**
   * Handle the final step.  Just mark it as viewed and then destroy
   */
  function handleGotItClick() {
    markAsViewed(steps[currentStep]);
    destroy();
  }

  /**
   * Move onto the next element to be highlighted
   */
  function handleNextBtnClick() {
    if (currentStep < steps.length - 1) {
      // mark the currentstep as seen
      markAsViewed(steps[currentStep]);

      removeHighlight(steps[currentStep]);
      currentStep++;
      highlightElement(steps[currentStep]);
    }
  }

  /**
   * Move onto the previous element to be highlighted
   */
  function handlePrevBtnClick() {
    if (currentStep > 0 && currentStep < steps.length - 1) {
      removeHighlight(steps[currentStep]);
      currentStep--;
      highlightElement(steps[currentStep]);
    }
  }

  function onScrollEvent() {
    if (steps[currentStep]) {
      setHighlighterOverlay(steps[currentStep]);
    }
  }


  /**
   * Remove all the tutorial stuff since we're done with it.
   */
  function destroy() {
    if (!_.isEmpty(steps)) {
      removeHighlight(steps[currentStep]);
    }

    const mountPoint = document.getElementById("js-coachmark");
    if (mountPoint) {
      mountPoint.remove();
    }
    if (nextBtn) {
      nextBtn.removeEventListener("click", handleNextBtnClick);
    }
    if (prevBtn) {
      prevBtn.removeEventListener("click", handlePrevBtnClick);
    }
    if (skipBtn) {
      skipBtn.removeEventListener("click", handleSkipClick);
    }
    // Destroy all dynamically inserted styles
    const allDynamicStyles = document.getElementsByClassName(styleClass);
    if (allDynamicStyles.length) {
      Array.from(allDynamicStyles).forEach(dynamicStyle => dynamicStyle.remove());
    }

    window.removeEventListener(scroll, onScrollEvent);

    tutIsRunning = false;
  }

  /**
   * Updated localstorage and the current element being viewed.  The step viewed is stored as its step number keyed by the
   * tutorial type.
   *
   * @param {*} currentStepElement
   */
  function markAsViewed(currentStepElement) {
    const viewed = JSON.parse(storage.getItem(storage.TUTORIAL_VIEWED) || JSON.stringify({}));
    const typeViewed = viewed[options.type] || [];
    const currentStepNumber = Number.parseInt(currentStepElement.dataset.coachmark, 10);
    if (!_.includes(typeViewed, currentStepNumber)) {
      typeViewed.push(currentStepNumber);
      viewed[options.type] = typeViewed;
      storage.setItem(storage.TUTORIAL_VIEWED, JSON.stringify(viewed));
    }
  }

  function getCoachmarkElements() {
    return `
      <section id="js-coachmark">
      <section id="js-coachmark-overlay-top" class="coachmark-overlay-top"></section>
      <section id="js-coachmark-overlay-bottom" class="coachmark-overlay-bottom"></section>
      <section id="js-coachmark-overlay-left" class="coachmark-overlay-left"></section>
      <section id="js-coachmark-overlay-right" class="coachmark-overlay-right"></section>
      <section id="js-coachmark-interface" class="coachmark-interface"></section>
        <section id="js-coachmark-tooltip" class="coachmark-tooltip">
          <header id="js-coachmark-tooltip-text">This is the intro text</header>
          <article class="coachmark-btns">
            <button class="coachmark-skip btn btn-danger">Skip</button>
            <div>
              <button class="coachmark-prev btn btn-secondary mr-3">Prev</button>
              <button class="coachmark-next btn btn-primary">Next</button>
            </div>
            <button class="coachmark-gotit btn btn-success">Got it</button>
          </article>
        </section>
      </section>
    `;
  }

  function addStyles(selector, styles) {
    if (styles.length) {
      const style = document.createElement("style");
      style.classList.add(styleClass);
      document.head.appendChild(style);
      const styleSheet = style.sheet;
      styleSheet.insertRule(
        `${selector} { ${styles} }`,
        styleSheet.cssRules.length
      );
    }
  }

  function sortArr(arr) {
    if (!Array.isArray(arr) || arr.length === 0) {
      return [];
    }
    arr.sort((a, b) => {
      const aValue = Number.parseInt(a.dataset.coachmark, 10);
      const bValue = Number.parseInt(b.dataset.coachmark, 10);
      return aValue - bValue;
    });
    return [...arr];
  }

  function isRunning() {
    return tutIsRunning;
  }

  /**
   * Adjusts the selected highlighted area due to a DOM change that might have shifted elements around
   * after starting the tutorial.  Basically re-highlights the element that is currently selected.
   */
  function adjust() {
    if (isRunning()) {
      highlightElement(steps[currentStep]);
    }
  }

  return {
    init: init,
    destroy: destroy,
    isRunning: isRunning,
    adjust
  };
}

module.exports = Coachmark;
